import React from 'react';
import { LogIn, LogOut, BedDouble } from 'lucide-react';

// "Today's Front Desk" beside the room calendar. Everything here is worked out
// from the booking rows and rooms the Bookings screen already loaded — it adds
// no request and changes nothing; the buttons call the same Check-In /
// Check-Out handlers the list uses (so check-out still opens Record Payment).
//
// Counts come from the BOOKINGS, never from a room's stored status: a booking
// marks its room 'occupied' the moment it is made, even for a stay next month,
// so that status can't say who is in the house tonight.

const BLOCKED_ROOM = ['locked', 'maintenance', 'out_of_order'];
const day = (v) => (v ? String(v).slice(0, 10) : '');
const DAY_MS = 86400000;
const nightsBetween = (a, b) => Math.max(1, Math.round((Date.parse(`${day(b)}T00:00:00Z`) - Date.parse(`${day(a)}T00:00:00Z`)) / DAY_MS) || 1);

export function localToday() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// A booking holds its room tonight when it has started and has not ended. A
// guest due out today does not hold tonight; a checked-in guest past their
// date, or on an open stay, still does.
function holdsTonight(b, today) {
  if (!['pending', 'confirmed', 'checked_in'].includes(b.status)) return false;
  if (day(b.check_in) > today) return false;
  if (b.status === 'checked_in') return b.open_ended ? true : day(b.check_out) !== today;
  return b.open_ended ? true : day(b.check_out) > today;
}

export function frontDeskSummary(bookings, rooms, today) {
  const list = Array.isArray(bookings) ? bookings : [];
  const arrivals = list
    .filter((b) => ['pending', 'confirmed'].includes(b.status) && day(b.check_in) === today)
    .sort((a, b) => String(a.guest_name).localeCompare(String(b.guest_name)));
  const departures = list
    .filter((b) => b.status === 'checked_in' && !b.open_ended && day(b.check_out) && day(b.check_out) <= today)
    .sort((a, b) => day(a.check_out).localeCompare(day(b.check_out)));
  const inHouse = list.filter((b) => b.status === 'checked_in');
  const roomList = Array.isArray(rooms) ? rooms : [];
  const held = new Set(list.filter((b) => holdsTonight(b, today)).map((b) => String(b.room_id)));
  const blocked = roomList.filter((r) => BLOCKED_ROOM.includes(r.status));
  const sellable = roomList.filter((r) => !BLOCKED_ROOM.includes(r.status));
  const occupiedTonight = sellable.filter((r) => held.has(String(r.id))).length;
  // A room being cleaned with nobody due in tonight; one that is also held
  // tonight counts as occupied, so the four readiness figures add up to the
  // total with no overlap.
  const cleaning = sellable.filter((r) => r.status === 'cleaning' && !held.has(String(r.id))).length;
  const freeTonight = sellable.length - occupiedTonight;
  return {
    arrivals,
    departures,
    inHouse,
    totalRooms: roomList.length,
    outOfService: blocked.length,
    occupiedTonight,
    freeTonight,
    cleaning,
    ready: freeTonight - cleaning,
  };
}

function Row({ b, today, action, owes, kes, onOpen }) {
  const due = owes(b);
  const late = b.status === 'checked_in' && day(b.check_out) < today;
  const n = nightsBetween(b.check_in, b.check_out);
  return (
    <li className="bp-guest">
      <button type="button" onClick={() => onOpen(b.id)} className="bp-guest-main">
        <strong>{b.guest_name}</strong>
        <span>
          Room {b.room_number} · {b.open_ended ? 'open stay' : `${n} night${n > 1 ? 's' : ''}`}
          {late && <em className="bp-bad"> · overdue</em>}
        </span>
        {due > 0 && <span className="bp-bad bp-owes-line">Owes {kes(due)}</span>}
      </button>
      {action}
    </li>
  );
}

function Section({ title, icon: Icon, tone, count, empty, grow, children }) {
  return (
    <section className={`bp-desk-section ${tone} ${grow ? 'bp-grow' : ''}`}>
      <h3><Icon size={15} />{title} ({count})</h3>
      {count === 0 ? <p className="bp-desk-empty">{empty}</p> : <ul>{children}</ul>}
    </section>
  );
}

export default function FrontDeskPanel({ summary, today, owes, kes, onOpen, onCheckIn, onCheckOut }) {
  const dateText = new Date(`${today}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <aside className="bp-card bp-desk" aria-label="Today's front desk">
      <header className="bp-desk-head">
        <h2>Today&apos;s Front Desk</h2>
        <p>{dateText}</p>
      </header>
      <Section title="Arrivals" icon={LogIn} tone="bp-t-green" count={summary.arrivals.length} empty="No arrivals expected today.">
        {summary.arrivals.map((b) => (
          <Row key={b.id} b={b} today={today} owes={owes} kes={kes} onOpen={onOpen}
            action={<button type="button" onClick={() => onCheckIn(b.id)} className="bp-act bp-act-green">Check in</button>} />
        ))}
      </Section>
      <Section title="Departures" icon={LogOut} tone="bp-t-red" count={summary.departures.length} empty="No departures due today.">
        {summary.departures.map((b) => (
          <Row key={b.id} b={b} today={today} owes={owes} kes={kes} onOpen={onOpen}
            action={<button type="button" onClick={() => onCheckOut(b.id)} className="bp-act bp-act-blue">Check out</button>} />
        ))}
      </Section>
      <Section title="In house" icon={BedDouble} tone="bp-t-blue" count={summary.inHouse.length} empty="Nobody is checked in." grow>
        {summary.inHouse.map((b) => (
          <Row key={b.id} b={b} today={today} owes={owes} kes={kes} onOpen={onOpen}
            action={<button type="button" onClick={() => onOpen(b.id)} className="bp-act bp-act-ghost">View</button>} />
        ))}
      </Section>
    </aside>
  );
}
